import React, { useState } from "react";

import {
  CheckCircle,
  Error as ErrorIcon,
  AccessTime,
  Launch as LaunchIcon,
  Info as InfoIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { ClusterApp, ClusterStatus } from "../types/cluster";

import AppDetails from "./appdetails";

interface Props {
  apps: ClusterApp[];
  cluster: ClusterStatus;
}

const statusStyles = {
  ok: {
    label: "ready",
    icon: <CheckCircle fontSize="small" />,
    fg: "var(--ok)",
    bg: "var(--ok-bg)",
    dot: "var(--ok)",
  },
  err: {
    label: "failed",
    icon: <ErrorIcon fontSize="small" />,
    fg: "var(--err)",
    bg: "var(--err-bg)",
    dot: "var(--err)",
  },
  pending: {
    label: "deploying",
    icon: <AccessTime fontSize="small" />,
    fg: "var(--warn)",
    bg: "var(--warn-bg)",
    dot: "var(--warn)",
  },
};

const statusFor = (app: ClusterApp): keyof typeof statusStyles => {
  if (app.status.error) return "err";
  if (app.status.final) return "ok";
  return "pending";
};

const StatusPill: React.FC<{ status: keyof typeof statusStyles }> = ({ status }) => {
  const s = statusStyles[status];
  return (
    <Chip
      icon={s.icon}
      label={s.label}
      size="small"
      sx={{
        height: 22,
        fontSize: 12,
        fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
        fontWeight: 500,
        borderRadius: 999,
        color: s.fg,
        backgroundColor: s.bg,
        border: 0,
        "& .MuiChip-icon": { color: s.fg, ml: 0.75, fontSize: 14 },
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

const ServiceStatusList: React.FC<Props> = ({ apps, cluster }) => {
  const counts = apps.reduce(
    (acc, a) => {
      const s = statusFor(a);
      acc[s] += 1;
      return acc;
    },
    { ok: 0, err: 0, pending: 0 },
  );
  const total = apps.length;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Services</Typography>
          <Typography
            sx={{
              fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            {counts.ok} ready · {counts.pending} deploying
            {counts.err > 0 ? ` · ${counts.err} failed` : ""}
          </Typography>
        </Stack>

        {total > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              height: 6,
              flex: { xs: 0, sm: 1 },
              maxWidth: { sm: 200 },
              minWidth: { sm: 120 },
            }}
          >
            {counts.ok > 0 && (
              <Box
                title={`${counts.ok} ready`}
                sx={{
                  flex: counts.ok,
                  borderRadius: 999,
                  backgroundColor: "var(--ok)",
                }}
              />
            )}
            {counts.pending > 0 && (
              <Box
                title={`${counts.pending} deploying`}
                sx={{
                  flex: counts.pending,
                  borderRadius: 999,
                  backgroundColor: "var(--warn)",
                }}
              />
            )}
            {counts.err > 0 && (
              <Box
                title={`${counts.err} failed`}
                sx={{
                  flex: counts.err,
                  borderRadius: 999,
                  backgroundColor: "var(--err)",
                }}
              />
            )}
          </Box>
        )}
      </Box>

      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        <Box
          component="thead"
          sx={{
            "& th": {
              textAlign: "left",
              padding: "10px 16px",
              fontFamily: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "text.secondary",
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "var(--bg-sunken)",
            },
          }}
        >
          <tr>
            <th>Service</th>
            <th>Status</th>
            <th>Endpoints</th>
            <th style={{ width: 1 }}></th>
          </tr>
        </Box>
        <tbody>
          {apps.length === 0 && (
            <tr>
              <td
                colSpan={4}
                style={{
                  padding: "32px 16px",
                  textAlign: "center",
                  color: "var(--fg-3)",
                  fontFamily:
                    "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                  fontSize: 13,
                }}
              >
                No services installed yet. Use{" "}
                <code
                  style={{
                    color: "var(--fg-1)",
                    backgroundColor: "var(--bg-sunken)",
                    padding: "0.1em 0.35em",
                    borderRadius: 2,
                  }}
                >
                  mu apps add
                </code>{" "}
                to deploy one.
              </td>
            </tr>
          )}
          {apps.map((app) => (
            <ServiceRow key={app.name} app={app} cluster={cluster} />
          ))}
        </tbody>
      </Box>
    </Box>
  );
};

const ServiceRow: React.FC<{ app: ClusterApp; cluster: ClusterStatus }> = ({ app, cluster }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [namespace = app.name] = app.name.split("/");
  const status = statusFor(app);
  const hrefs = app.status.hrefs || app.status.href || [];
  const firstHref = hrefs[0];

  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <Box
        component="tr"
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          transition: "background-color 120ms cubic-bezier(.2,.8,.2,1)",
          "&:last-of-type": { borderBottom: 0 },
          "&:hover": { backgroundColor: "var(--bg-sunken)" },
          "& td": {
            padding: "12px 16px",
            verticalAlign: "middle",
          },
        }}
      >
        <td>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: 2,
                backgroundColor: statusStyles[status].dot,
              }}
            />
            <Typography
              sx={{
                fontFamily:
                  "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                fontSize: 14,
                fontWeight: 500,
                color: "text.primary",
              }}
            >
              {namespace}
            </Typography>
          </Stack>
        </td>
        <td>
          <StatusPill status={status} />
        </td>
        <td>
          {hrefs.length === 0 ? (
            <Typography
              sx={{
                fontFamily:
                  "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                fontSize: 12,
                color: "text.secondary",
              }}
            >
              —
            </Typography>
          ) : hrefs.length === 1 ? (
            <Link
              href={firstHref}
              target="_blank"
              rel="noopener"
              sx={{
                fontFamily:
                  "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                fontSize: 13,
                color: "secondary.dark",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {new URL(firstHref).hostname}
              <LaunchIcon sx={{ fontSize: 12 }} />
            </Link>
          ) : (
            <>
              <Link
                component="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                  setAnchorEl(e.currentTarget)
                }
                sx={{
                  fontFamily:
                    "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                  fontSize: 13,
                  color: "secondary.dark",
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                  p: 0,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {hrefs.length} endpoints
              </Link>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={closeMenu}
                transformOrigin={{ horizontal: "left", vertical: "top" }}
                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                PaperProps={{
                  sx: {
                    maxWidth: 360,
                    maxHeight: 400,
                    overflow: "auto",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow:
                      "0 6px 16px rgba(16, 26, 47, 0.08), 0 12px 28px rgba(16, 26, 47, 0.08)",
                  },
                }}
              >
                {hrefs.map((link) => (
                  <MenuItem
                    key={link}
                    component="a"
                    href={link}
                    target="_blank"
                    rel="noopener"
                    onClick={closeMenu}
                    sx={{
                      fontFamily:
                        "'IBM Plex Mono', ui-monospace, Menlo, monospace",
                      fontSize: 13,
                      color: "secondary.dark",
                    }}
                  >
                    <LaunchIcon sx={{ fontSize: 14, mr: 1 }} />
                    {new URL(link).hostname}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </td>
        <td>
          <Tooltip title="Details">
            <IconButton
              size="small"
              onClick={() => setDetailsOpen(true)}
              sx={{ color: "text.secondary" }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </td>
      </Box>

      <AppDetails
        app={app}
        cluster={cluster}
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
      />
    </>
  );
};

export default ServiceStatusList;
